import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { viewCycleReportAPI } from "api/apiHelper";
import Card from "components/Card";
import Button from "components/Button";
import Backdrop from "components/Backdrop";
import ErrorBox from "components/ErrorBox";
import { toast } from "react-toastify";
import { MdArrowBack, MdDownload } from "react-icons/md";
import { generateCycleReportAPI } from "api/apiHelper";

interface ReportResult {
  employee: {
    id: number;
    full_name: string;
  };
  employee_id: number;
  employee_name: string;
  scores: {
    self?: number;
    manager?: number;
    peer?: number;
    peer_count?: number;
    client?: number;
  };
  final_score: number;
  appraisal_percentage: number;
}

interface CycleReport {
  cycle: {
    id: number;
    name: string;
    description: string;
    form_opens: string;
    deadline: string;
    locked: boolean;
    enable_self_review: boolean;
    enable_manager_review: boolean;
    enable_peer_review: boolean;
    enable_client_review: boolean;
    self_review_weightage: string;
    manager_review_weightage: string;
    peer_review_weightage: string;
    client_review_weightage: string;
  };
  results: ReportResult[];
  generated_at: string;
}

const ViewCycleReport = () => {
  const { cycleId } = useParams<{ cycleId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<CycleReport | null>(null);

  useEffect(() => {
    if (cycleId) {
      fetchReport();
    }
  }, [cycleId]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await viewCycleReportAPI(Number(cycleId));
      setReport(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to load report");
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!cycleId || !report) return;

    try {
      const blob = await generateCycleReportAPI(Number(cycleId));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `appraisal_report_${report.cycle.name.replace(/\s+/g, '_')}_${cycleId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Report downloaded successfully");
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to generate report");
    }
  };

  if (loading) {
    return <Backdrop show={true} />;
  }

  if (error || !report) {
    return (
      <div className="mt-5">
        <Card extra="p-6">
          <ErrorBox errorMessage={error || "Report not found"} />
          <div className="mt-4 flex justify-end">
            <Button
              varient="secondary"
              onClick={() => navigate('/manager-appraisals?tab=2')}
            >
              Back to Reports
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mt-5">
      <div className="mb-6 flex items-center justify-between">
        <Button
          varient="secondary"
          onClick={() => navigate('/manager-appraisals?tab=2')}
          className="flex items-center gap-2 px-4 py-2"
        >
          <MdArrowBack className="h-5 w-5" />
          Back to Reports
        </Button>
        <Button
          varient="primary"
          onClick={handleDownloadReport}
          className="flex items-center gap-2 px-6 py-2"
        >
          <MdDownload className="h-5 w-5" />
          Download PDF
        </Button>
      </div>

      <Card extra="p-6 mb-6">
        <div className="space-y-4">
          <div>
            <h2 className="text-2xl font-bold text-navy-700 dark:text-white mb-2">
              {report.cycle.name}
            </h2>
            {report.cycle.description && (
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {report.cycle.description}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Form Opens</p>
              <p className="text-base font-medium text-navy-700 dark:text-white">
                {new Date(report.cycle.form_opens).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Deadline</p>
              <p className="text-base font-medium text-navy-700 dark:text-white">
                {new Date(report.cycle.deadline).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Generated At</p>
              <p className="text-base font-medium text-navy-700 dark:text-white">
                {new Date(report.generated_at).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
              <span className="px-3 py-1 rounded-full text-sm font-semibold bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                Locked
              </span>
            </div>
          </div>
        </div>
      </Card>

      <Card extra="p-6">
        <h3 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
          Final Scores
        </h3>
        {report.results.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-400 py-10">
            No appraisal data available for this cycle.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-navy-50 dark:bg-navy-800">
                  <th className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-left text-sm font-semibold text-navy-700 dark:text-white">
                    Rank
                  </th>
                  <th className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-left text-sm font-semibold text-navy-700 dark:text-white">
                    Employee Name
                  </th>
                  {report.cycle.enable_self_review && (
                    <th className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-center text-sm font-semibold text-navy-700 dark:text-white">
                      Self Review
                    </th>
                  )}
                  {report.cycle.enable_manager_review && (
                    <th className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-center text-sm font-semibold text-navy-700 dark:text-white">
                      Manager Review
                    </th>
                  )}
                  {report.cycle.enable_peer_review && (
                    <th className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-center text-sm font-semibold text-navy-700 dark:text-white">
                      Peer Review
                    </th>
                  )}
                  {report.cycle.enable_client_review && (
                    <th className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-center text-sm font-semibold text-navy-700 dark:text-white">
                      Client Review
                    </th>
                  )}
                  <th className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-center text-sm font-semibold text-navy-700 dark:text-white bg-green-100 dark:bg-green-900/30">
                    Final Score
                  </th>
                  <th className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-center text-sm font-semibold text-navy-700 dark:text-white bg-blue-100 dark:bg-blue-900/30">
                    Appraisal %
                  </th>
                </tr>
              </thead>
              <tbody>
                {report.results.map((result, index) => (
                  <tr
                    key={result.employee_id}
                    className="hover:bg-gray-50 dark:hover:bg-navy-800/50"
                  >
                    <td className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-sm text-navy-700 dark:text-white">
                      {index + 1}
                    </td>
                    <td className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-sm font-medium text-navy-700 dark:text-white">
                      {result.employee_name}
                    </td>
                    {report.cycle.enable_self_review && (
                      <td className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-center text-sm text-navy-700 dark:text-white">
                        {result.scores.self !== undefined ? (
                          <span className="font-semibold">{result.scores.self}</span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                    )}
                    {report.cycle.enable_manager_review && (
                      <td className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-center text-sm text-navy-700 dark:text-white">
                        {result.scores.manager !== undefined ? (
                          <span className="font-semibold">{result.scores.manager}</span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                    )}
                    {report.cycle.enable_peer_review && (
                      <td className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-center text-sm text-navy-700 dark:text-white">
                        {result.scores.peer !== undefined ? (
                          <div>
                            <span className="font-semibold">{result.scores.peer}</span>
                            {result.scores.peer_count && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                                ({result.scores.peer_count})
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                    )}
                    {report.cycle.enable_client_review && (
                      <td className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-center text-sm text-navy-700 dark:text-white">
                        {result.scores.client !== undefined ? (
                          <span className="font-semibold">{result.scores.client}</span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                    )}
                    <td className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-center text-sm font-bold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20">
                      {result.final_score}
                    </td>
                    <td className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-center text-sm font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20">
                      {result.appraisal_percentage}%
                    </td>
                  </tr>
                ))}
                <tr className="bg-green-50 dark:bg-green-900/20 font-bold">
                  <td 
                    colSpan={
                      2 + 
                      (report.cycle.enable_self_review ? 1 : 0) +
                      (report.cycle.enable_manager_review ? 1 : 0) +
                      (report.cycle.enable_peer_review ? 1 : 0) +
                      (report.cycle.enable_client_review ? 1 : 0)
                    }
                    className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-right text-sm font-bold text-navy-700 dark:text-white"
                  >
                    Average:
                  </td>
                  <td className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-center text-sm font-bold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30">
                    {report.results.length > 0 
                      ? (report.results.reduce((sum, r) => sum + r.final_score, 0) / report.results.length).toFixed(2)
                      : '0.00'}
                  </td>
                  <td className="border border-gray-200 dark:border-gray-700 px-4 py-3 text-center text-sm font-bold text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30">
                    {report.results.length > 0
                      ? (report.results.reduce((sum, r) => sum + r.appraisal_percentage, 0) / report.results.length).toFixed(2)
                      : '0.00'}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ViewCycleReport;
